import React from 'react';
import { CircularProgress, Typography, Box, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Universal loading component for pages
 * Provides consistent loading states across the application
 */
const PageLoader = ({ 
  loading, 
  children, 
  loadingText = null,
  variant = 'spinner', // 'spinner', 'skeleton'
  minHeight = '400px',
  showHeader = true,
  headerTitle = '',
  headerSubtitle = ''
}) => {
  const { t } = useTranslation();

  if (!loading) {
    return children;
  }

  const defaultLoadingText = loadingText || t('generic.loadingData');

  if (variant === 'skeleton') {
    return (
      <div style={{ padding: '24px' }}>
        {showHeader && (
          <>
            <Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="300px" height={24} sx={{ mb: 3 }} />
          </>
        )}
        <Skeleton variant="rectangular" height="60px" sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height="300px" />
      </div>
    );
  }

  return (
    <Box 
      display="flex" 
      flexDirection="column"
      minHeight={minHeight}
    >
      {showHeader && (headerTitle || headerSubtitle) && (
        <Box sx={{ mb: 3 }}>
          {headerTitle && (
            <Typography variant="h5" fontWeight="bold">
              {headerTitle}
            </Typography>
          )}
          {headerSubtitle && (
            <Typography variant="body2" color="text.secondary">
              {headerSubtitle}
            </Typography>
          )}
        </Box>
      )}
      
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        flex={1}
      >
        <div style={{ textAlign: 'center' }}>
          <CircularProgress size={48} thickness={4} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            {defaultLoadingText}
          </Typography>
        </div>
      </Box>
    </Box>
  );
};

/**
 * Loading overlay for full-screen loading states
 */
export const LoadingOverlay = ({ loading, children, loadingText = null }) => {
  const { t } = useTranslation();

  if (!loading) {
    return children;
  }

  return (
    <>
      {children}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <CircularProgress size={48} thickness={4} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            {loadingText || t('generic.loadingData')}
          </Typography>
        </div>
      </div>
    </>
  );
};

export default PageLoader;